import { useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/** One member's last known position, from GET /events/:id/team-locations. */
export interface TeamMemberLocation {
  memberId: string;
  displayName: string;
  isLeader: boolean;
  /** False once the fix has aged past the server's staleness window. */
  live: boolean;
  latitude: number | null;
  longitude: number | null;
  accuracyMeters: number | null;
  updatedAt: string | null;
}

/** A team's position on the live map: one pin, plus the members behind it. */
export interface TeamLocation {
  teamId: string;
  name: string;
  emoji: string | null;
  iconName: string;
  iconColor: string;
  photoUrl: string | null;
  /** Null when no member of the team has a current fix. */
  position: {
    latitude: number;
    longitude: number;
    accuracyMeters: number | null;
    updatedAt: string | null;
    reportedBy: { memberId: string; displayName: string };
  } | null;
  /**
   * Widest distance between any two reporting members, or null when fewer
   * than two are live. This is what tells a facilitator a team has split.
   */
  spreadMeters: number | null;
  members: TeamMemberLocation[];
}

export const TEAM_LOCATIONS_QUERY_KEY = 'EventTeamLocations';

/**
 * Positions move continuously and there is no socket event for them, so
 * unlike the leaderboard this poll is the only refresh. Kept at 15s to
 * match the interval the phones report on — polling faster would just
 * re-fetch identical rows.
 */
const TEAM_LOCATIONS_POLL_INTERVAL_MS = 15_000;

/**
 * Grant a team a time-boxed pass on the stay-together rule.
 *
 * The escape hatch for when the rule is wrong and a team is standing right
 * in front of staff. Invalidates the locations query so the split banner
 * clears without waiting for the next poll.
 */
export function useGrantTogetherOverride(eventId: string | undefined) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ teamId, minutes }: { teamId: string; minutes: number }) =>
      apiClient.post(`/events/${eventId}/teams/${teamId}/together-override`, { minutes }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [TEAM_LOCATIONS_QUERY_KEY, eventId] });
    },
  });
}

export function useTeamLocations(eventId: string | undefined) {
  const apiClient = useApiClient();
  return useQuery<{ success: boolean; data: TeamLocation[] }>({
    queryKey: [TEAM_LOCATIONS_QUERY_KEY, eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/team-locations`),
    enabled: Boolean(eventId),
    refetchInterval: TEAM_LOCATIONS_POLL_INTERVAL_MS,
    staleTime: 5_000,
  });
}
